import { DefaultValues, FieldErrors, FieldValues, FormState, useForm, UseFormProps, UseFormReturn, UseFormSetValue } from 'react-hook-form';
import { getDirtyValues } from '../utils/getDirtyValues';
import { getDirtyFieldNames } from '../utils/getDirtyFieldNames';
import { BaseSyntheticEvent } from 'react';
import { mergeDirtyFieldsWithSkipped } from '../utils/mergeDirtyFieldsWithSkipped';

export type DirtySubmitHandler<T extends FieldValues> = (
  data: Partial<T>
) => void | Promise<void>;

export type CustomFormState<TFieldValues extends FieldValues = FieldValues> =
  FormState<TFieldValues> & {
    isAnyDirty: boolean;
  };

export type SkipFieldsMap<T> = T extends Array<infer U>
  ? Array<SkipFieldsMap<U>> | boolean
  : T extends object
  ? { [K in keyof T]?: SkipFieldsMap<T[K]> | boolean }
  : boolean;

export type UseFormResetDirtyFields<TFieldValues> = (
  values?: TFieldValues | DefaultValues<TFieldValues> | undefined
) => void

export type UseCustomFormReturn<
  TFieldValues extends FieldValues = FieldValues,
  TOutput extends Record<string, any> = Partial<TFieldValues>
> = Omit<UseFormReturn<TFieldValues, TOutput>, 'formState'> & {
  formState: CustomFormState<TFieldValues>;
  handleDirtySubmit: (
    onValid: DirtySubmitHandler<TOutput>,
    onError?: (errors: FieldErrors<TFieldValues>) => void,
    onEmptyDirtyFields?: () => void
  ) => (e?: BaseSyntheticEvent) => Promise<void>;
  resetDirtyFields: (
    values?: TFieldValues | DefaultValues<TFieldValues> | undefined
  ) => void;
};

type UseCustomFormProps<TFieldValues> = {
  shouldOnlyTriggerDirtyFieldsOnSubmit?: boolean;
  resetDirtyFieldsAfterSubmit?: boolean;
  skipNotDirtyFieldsOnSubmit?: Partial<Readonly<SkipFieldsMap<TFieldValues>>>;
  extVariablesToCheckDirtyState?: any[];
}

export const useCustomForm = <
  TFieldValues extends FieldValues = FieldValues,
  TOutput extends Record<string, any> = Partial<TFieldValues>
>(
  config: UseFormProps<TFieldValues, TOutput> & UseCustomFormProps<TFieldValues> = {
    shouldOnlyTriggerDirtyFieldsOnSubmit: false,
    resetDirtyFieldsAfterSubmit: false,
  }
): UseCustomFormReturn<TFieldValues, TOutput> => {
  const methods = useForm<TFieldValues>(config);

  const { dirtyFields, errors } = methods.formState;

  // custom methods
  const isAnyDirty = !!(
    Object.keys(dirtyFields).some(Boolean) || config.extVariablesToCheckDirtyState?.some(Boolean)
  );

  (methods.formState as CustomFormState<TFieldValues>).isAnyDirty = isAnyDirty;

  const resetDirtyFields = (values?: TFieldValues | DefaultValues<TFieldValues> | undefined) => methods.reset(values, {
    keepErrors: !values,
    keepDirty: false,
    keepDirtyValues: false,
    keepValues: !values,
    keepDefaultValues: false,
    keepIsSubmitted: false,
    keepTouched: !values,
    keepIsValid: false,
    keepSubmitCount: true,
  });

  const handleDirtySubmit = (
    onValid: DirtySubmitHandler<TOutput>,
    onError?: (errors: FieldErrors<TFieldValues>) => void,
    onEmptyDirtyFields?: () => void,
  ) => async (e?: BaseSyntheticEvent) => {
    e?.preventDefault?.();

    const dirtyFieldNames = getDirtyFieldNames(dirtyFields as Partial<Record<keyof TFieldValues, any>>);

    const isValid = await methods.trigger(config.shouldOnlyTriggerDirtyFieldsOnSubmit ? dirtyFieldNames : undefined);

    if (!isValid) {
      return onError?.(errors);
    }

    methods.clearErrors(dirtyFieldNames);

    const allData = methods.watch();

    const combinedDirtyFields = mergeDirtyFieldsWithSkipped(
      dirtyFields,
      config.skipNotDirtyFieldsOnSubmit as Record<string, any> | undefined
    ) as Record<keyof TFieldValues, any>;

    const dirtyData = await getDirtyValues(combinedDirtyFields, allData);

    const internalIsAnyDirty = !!(
      Object.keys(combinedDirtyFields).some(Boolean) || config.extVariablesToCheckDirtyState?.some(Boolean)
    );

    if (internalIsAnyDirty) {
      await onValid(dirtyData as TOutput);

      if (config.resetDirtyFieldsAfterSubmit) {
        resetDirtyFields();
      }
    } else {
      return onEmptyDirtyFields?.();
    }
  };

  // default methods
  const setValue: UseFormSetValue<TFieldValues> = (name, value, options) =>
    methods.setValue(name, value, { shouldDirty: true, ...options });

  return {
    ...methods,
    formState: methods.formState as CustomFormState<TFieldValues>,
    handleDirtySubmit,
    resetDirtyFields,
    setValue,
  } as UseCustomFormReturn<TFieldValues, TOutput>;
}
